import { createAsyncThunk } from '@reduxjs/toolkit';
import { Section, JobPhasesApiResponse } from '@/redux/slices/jobPhasesSlice';
import {
  API_JOB_PHASES,
  API_CREATE_JOB_PHASE,
  API_METHOD_GET,
  API_METHOD_POST,
} from '@/constants/apiConstants';
import { apiCall } from '@/utility/api/apiUtils';
import { Method } from 'axios';
import {
  updatePhaseServices,
  UpdatePhaseServicesPayload,
  UpdatePhaseServicesResponse,
} from '@/api/v2/phases/updatePhaseServices';
import {
  createTask,
  CreateTaskPayload,
  CreateTaskResponse,
} from '@/api/v2/phases/tasks/createTask';
import {
  updateTaskApi,
  UpdateTaskPayload,
  UpdateTaskResponse,
} from '@/api/v2/phases/tasks/updateTask';
import {
  deleteTaskApi,
  DeleteTaskResponse,
} from '@/api/v2/phases/tasks/deleteTask';
import {
  updatePhaseNameFromAPI,
  UpdatePhaseNameRequest,
  UpdatePhaseNameResponse,
} from '@/api/v2/phases/updatePhaseName';
import {
  deletePhaseFromAPI,
} from '@/api/v2/phases/deletePhase';

export interface FetchJobPhasesResponse extends JobPhasesApiResponse {}

export interface SaveJobPhasesResponse {
  status: 'SUCCESS' | 'ERROR';
  message?: string;
}

export interface CreateJobPhaseResponse {
  status: 'SUCCESS' | 'ERROR';
  data?: any;
  message?: string;
}

export interface DeleteResponse {
  status: 'SUCCESS' | 'ERROR';
  message?: string;
}

export interface DeletePayload {
  type: 'phase' | 'section' | 'item';
  uuid: string;
}

// Real API function to fetch job phases
const fetchJobPhasesFromAPI = async (
  jobUuid: string,
): Promise<FetchJobPhasesResponse> => {
  const method: Method = API_METHOD_GET;
  const url = API_JOB_PHASES(jobUuid);

  try {
    const response = await apiCall({
      method,
      url,
    });
    return response;
  } catch (error) {
    console.error('Error fetching job phases:', error);
    throw error;
  }
};

// Create phase API function
const createJobPhaseFromAPI = async (
  phaseName?: string,
): Promise<CreateJobPhaseResponse> => {
  const method: Method = API_METHOD_POST;
  // Extract jobUuid from current URL
  const pathSegments = window.location.pathname.split('/');
  const jobUuid = pathSegments[pathSegments.indexOf('jobs') + 1];
  const url = API_CREATE_JOB_PHASE(jobUuid);

  try {
    const response = await apiCall({
      method,
      url,
      // Do not send a name in the payload; backend should assign defaults
      data: {},
    });
    return response;
  } catch (error) {
    console.error('Error creating job phase:', error);
    throw error;
  }
};

// Delete API function (same URL, different payload)
const deleteFromAPI = async (
  phaseUuid: string,
  payload: DeletePayload,
): Promise<DeleteResponse> => {
  try {
    return await deletePhaseFromAPI(phaseUuid, payload);
  } catch (error) {
    console.error('Error deleting:', error);
    throw error;
  }
};

const saveJobPhasesToAPI = async (
  jobUuid: string,
  sections: Section[],
): Promise<SaveJobPhasesResponse> => {
  // TODO: Replace with actual API call
  // Example: return await saveJobPhases(jobUuid, sections);

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        status: 'SUCCESS',
        message: 'Job phases saved successfully',
      });
    }, 1000);
  });
};

// Async thunks
export const fetchJobPhases = createAsyncThunk<
  FetchJobPhasesResponse,
  string,
  { rejectValue: string }
>('jobPhases/fetchJobPhases', async (jobUuid: string, { rejectWithValue }) => {
  try {
    const response = await fetchJobPhasesFromAPI(jobUuid);
    return response;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch job phases',
    );
  }
});

export const saveJobPhases = createAsyncThunk<
  SaveJobPhasesResponse,
  { jobUuid: string; sections: Section[] },
  { rejectValue: string }
>(
  'jobPhases/saveJobPhases',
  async ({ jobUuid, sections }, { rejectWithValue }) => {
    try {
      const response = await saveJobPhasesToAPI(jobUuid, sections);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to save job phases',
      );
    }
  },
);

export const autoSaveJobPhases = createAsyncThunk<
  SaveJobPhasesResponse,
  { jobUuid: string; sections: Section[] },
  { rejectValue: string }
>(
  'jobPhases/autoSaveJobPhases',
  async ({ jobUuid, sections }, { rejectWithValue }) => {
    try {
      const response = await saveJobPhasesToAPI(jobUuid, sections);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to auto-save job phases',
      );
    }
  },
);
// Import job phases template
export const importJobPhasesTemplate = createAsyncThunk<
  JobPhasesApiResponse,
  void,
  { rejectValue: string }
>('jobPhases/importJobPhasesTemplate', async (_, { rejectWithValue }) => {
  try {
    // This would typically fetch a template from the API
    // For now, we'll return a basic template structure
    const templateData: Section[] = [
      {
        id: 'template-section-1',
        title: 'Template Section',
        description: 'Default template section',
        section_type: 1,
        checked: false,
        level: 0,
        phases: [],
        products_services: [],
      },
    ];

    return {
      status: 'SUCCESS',
      data: {
        phases: [],
        sections: templateData,
      },
    };
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to import template',
    );
  }
});

// Create job phase thunk
export const createJobPhase = createAsyncThunk<
  CreateJobPhaseResponse,
  string | undefined, // Optional phaseName, jobUuid comes from URL
  { rejectValue: string }
>('jobPhases/createJobPhase', async (phaseName, { rejectWithValue }) => {
  try {
    const response = await createJobPhaseFromAPI(phaseName);
    return response;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to create job phase',
    );
  }
});

// Update phase services thunk
export const updatePhaseServicesThunk = createAsyncThunk<
  UpdatePhaseServicesResponse,
  { phaseUuid: string; payload: UpdatePhaseServicesPayload },
  { rejectValue: string }
>(
  'jobPhases/updatePhaseServices',
  async ({ phaseUuid, payload }, { rejectWithValue }) => {
    try {
      const response = await updatePhaseServices(phaseUuid, payload);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update phase services',
      );
    }
  },
);

// Delete thunk (handles phase, section, and item deletion)
export const deleteThunk = createAsyncThunk<
  DeleteResponse,
  { phaseUuid: string; uuid: string; type: 'phase' | 'section' | 'item' },
  { rejectValue: string }
>(
  'jobPhases/delete',
  async ({ phaseUuid, uuid, type }, { rejectWithValue }) => {
    try {
      const payload: DeletePayload = { type, uuid };
      const response = await deleteFromAPI(phaseUuid, payload);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : `Failed to delete ${type}`,
      );
    }
  },
);

// Create task thunk
export const createTaskThunk = createAsyncThunk<
  CreateTaskResponse,
  CreateTaskPayload,
  { rejectValue: string }
>('jobPhases/createTask', async (payload, { rejectWithValue }) => {
  try {
    const response = await createTask(payload);
    return response;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to create task',
    );
  }
});

// Update task thunk
export const updateTaskApiThunk = createAsyncThunk<
  UpdateTaskResponse,
  { taskUuid: string; payload: UpdateTaskPayload },
  { rejectValue: string }
>(
  'jobPhases/updateTask',
  async ({ taskUuid, payload }, { rejectWithValue }) => {
    try {
      const response = await updateTaskApi(taskUuid, payload);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update task',
      );
    }
  },
);

// Delete task thunk
export const deleteTaskApiThunk = createAsyncThunk<
  DeleteTaskResponse,
  { taskUuid: string },
  { rejectValue: string }
>('jobPhases/deleteTask', async ({ taskUuid }, { rejectWithValue }) => {
  try {
    const response = await deleteTaskApi(taskUuid);
    return response;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to delete task',
    );
  }
});

// Update phase name thunk
export const updatePhaseNameThunk = createAsyncThunk<
  UpdatePhaseNameResponse,
  { phaseUuid: string; name: string },
  { rejectValue: string }
>(
  'jobPhases/updatePhaseName',
  async ({ phaseUuid, name }, { rejectWithValue }) => {
    try {
      const response = await updatePhaseNameFromAPI(phaseUuid, { name });
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update phase name',
      );
    }
  },
);
